import { sha256 } from 'js-sha256';

/**
 * Interace of visitor API
 */
interface VisitorAPI {
	/** Adobe property for internal use */
	AuthState: { AUTHENTICATED: number };
	/** Adobe property for internal use */
	getInstance: (orgId: string) => VisitorAPI;
	/** Adobe property for internal use */
	getVisitorValues: (callback: (values: VisitorValues) => void) => void;
	/** Adobe property for internal use */
	getMarketingCloudVisitorID: () => string | undefined;
	/** Adobe property for internal use */
	getCustomerIDs: () => { [propName: string]: string };
	/** Adobe property for internal use */
	setCustomerIDs: (values: { userId: { id: string; authState: number } }) => void;
}

/**
 * Interface of visitor values from adobe
 * @public
 */
export interface VisitorValues {
	/** Adobe property for internal use */
	MCAAMB: string;
	/** Adobe property for internal use */
	MCAAMLH: string;
	/** Adobe property for internal use */
	MCAID: string;
	/** Adobe property for internal use */
	MCMID: string;
	/** Adobe property for internal use */
	MCOPTOUT: boolean;
}

/**
 * Interface of id stitch object
 * @public
 */
export interface IdStitchObject {
	/** Unique organisation identity used by adobe */
	ADOBE_ORG_ID: string;
	/** Path to adobe's VisitorAPI.js release */
	VISITOR_API_JS_PATH: string;
	/** Email address of logged in user */
	emailAddress: string;
	/** SHA256 hash of email address */
	emailHash: string;
	/** Adobe's visitor Id */
	adobeEcid: string;
	/** Additional properties from adobe library */
	adobeProperties?: {
		adobeCustomerIds: object;
		adobeMarketingCloudVisitorID: string | undefined;
		adobeVisitorValues: VisitorValues;
	};
	/** Error stacktrace in case of error */
	error?: Event | string | unknown;
}

/** Unique organisation identity used by adobe */
const ADOBE_ORG_ID = 'A7D63BC75245AE300A490D4D@AdobeOrg';

/**
 * Visitor is the reference of Adobe VisitorAPI JS instance
 */
declare let Visitor: VisitorAPI;

/**
 * Method to get id stitch values
 * @param emailAddress - is the user email id used for login in application
 * @returns an object of id Stitch values
 * @public
 */
export async function getIdStitchValues(emailAddress: string): Promise<IdStitchObject> {
	return new Promise((resolve, reject) => {
		const idStitchObject: IdStitchObject = {
			ADOBE_ORG_ID,
			VISITOR_API_JS_PATH: 'https://github.com/Adobe-Marketing-Cloud/id-service/releases/download/5.2.0/visitorapi.min.js',
			emailAddress,
			emailHash: '',
			adobeEcid: '',
		};
		document.getElementById('id-stitch-visitorapi')?.remove();
		const script = document.createElement('script');
		script.src = idStitchObject.VISITOR_API_JS_PATH;
		script.defer = true;
		script.id = 'id-stitch-visitorapi';
		document.body.appendChild(script);
		script.onload = async () => {
			try {
				const emailHash = sha256(emailAddress);
				const visitor = Visitor.getInstance(idStitchObject.ADOBE_ORG_ID);
				const visitorValues: VisitorValues = await new Promise(resolve =>
					visitor.getVisitorValues((values: VisitorValues) => resolve(values)),
				);
				visitor.setCustomerIDs({ userId: { id: emailHash, authState: Visitor?.AuthState?.AUTHENTICATED } });
				Object.assign(idStitchObject, {
					emailHash,
					adobeEcid: visitorValues?.MCMID,
					adobeProperties: {
						adobeCustomerIds: visitor.getCustomerIDs(),
						adobeMarketingCloudVisitorID: visitor.getMarketingCloudVisitorID(),
						adobeVisitorValues: visitorValues,
					},
				});
				resolve(idStitchObject);
			} catch (error) {
				idStitchObject.error = error;
				reject(idStitchObject);
			}
		};

		script.onerror = _error => {
			idStitchObject.error = _error;
			reject(idStitchObject);
		};
	});
}

/**
 * Method to test health of Id stitch lib
 * @returns a string msessage
 * @public
 */
export function testHealth(): string {
	return `Id Stitch JS test successful`;
}

/** Global to access trIdStitch via window object */
declare global {
	interface Window {
		trIdStitch: {
			version: string;
			testHealth: () => void;
			getIdStitchValues: (emailAddress: string) => Promise<IdStitchObject>;
		};
	}
}

/**
 * Executing this method will add trIdStitch to window object
 * @remarks This is considered a side effect and should be avoided
 * @internal
 */
export function executeSideEffects(): void {
	/** Window reference object */
	const _window = window as Window;
	_window.trIdStitch = {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		version: (globalThis as any).VERSION,
		testHealth,
		getIdStitchValues,
	};
}
